import { z } from 'zod'

import * as PrimaryContractDeploymentSchema from './PrimaryContractDeploymentSchema'
import * as SecondaryContractDeploymentSchema from './SecondaryContractDeploymentSchema'
import * as ProfileAddressSchema from './ProfileAddressSchema'
import * as InitialControllerAddressSchema from './InitialControllerAddressSchema'
import * as InitialChainIDSchema from './InitialChainIDSchema'
import * as PostDeploymentModuleSchema from './PostDeploymentModuleSchema'
import * as PostDeploymentModuleCalldataSchema from './PostDeploymentModuleCalldataSchema'
import * as ReadyEncodedCrossDeployCalldataSchema from './ReadyEncodedCrossDeployCalldataSchema'
import * as LinkedContractsFactoryAddressSchema from '@/business-logic/wallet/domain/backup/reports/BackupReportV2/LinkedContractsFactoryAddressSchema'
import { CrossChainDeploymentPackage } from '../../../cross-chain-deployment/CrossChainDeploymentPackage'

export const CrossChainDeploymentPackageSchema = z.object({
  profileAddress: ProfileAddressSchema.ProfileAddressSchema,
  initialControllerAddress:
    InitialControllerAddressSchema.InitialControllerAddressSchema,
  initialChainID: InitialChainIDSchema.InitialChainIDSchema,
  primaryContractDeployment:
    PrimaryContractDeploymentSchema.PrimaryContractDeploymentSchema,
  secondaryContractDeployment:
    SecondaryContractDeploymentSchema.SecondaryContractDeploymentSchema,
  postDeploymentModule: PostDeploymentModuleSchema.PostDeploymentModuleSchema,
  postDeploymentModuleCalldata:
    PostDeploymentModuleCalldataSchema.PostDeploymentModuleCalldataSchema,
  readyEncodedCrossDeployCalldata:
    ReadyEncodedCrossDeployCalldataSchema.ReadyEncodedCrossDeployCalldataSchema,
  linkedContractsFactoryAddress:
    ReadyEncodedCrossDeployCalldataSchema.ReadyEncodedCrossDeployCalldataSchema,
})

type CrossChainDeploymentPackageReportText = z.infer<
  typeof CrossChainDeploymentPackageSchema
>

export function mapDomainToReportText(
  deploymentPackage: CrossChainDeploymentPackage
): CrossChainDeploymentPackageReportText {
  return {
    profileAddress: ProfileAddressSchema.mapDomainToReportText(
      deploymentPackage.getProfileAddress()
    ),
    initialControllerAddress:
      InitialControllerAddressSchema.mapDomainToReportText(
        deploymentPackage.getInitialControllerAddress()
      ),
    initialChainID: InitialChainIDSchema.mapDomainToReportText(
      deploymentPackage.getInitialChainID()
    ),
    primaryContractDeployment:
      PrimaryContractDeploymentSchema.mapDomainToReportText(
        deploymentPackage.getPrimaryContractDeployment()
      ),
    secondaryContractDeployment:
      SecondaryContractDeploymentSchema.mapDomainToReportText(
        deploymentPackage.getSecondaryContractDeployment()
      ),
    postDeploymentModule: PostDeploymentModuleSchema.mapDomainToReportText(
      deploymentPackage.getPostDeploymentModule()
    ),
    postDeploymentModuleCalldata:
      PostDeploymentModuleCalldataSchema.mapDomainToReportText(
        deploymentPackage.getPostDeploymentModuleCalldata()
      ),
    readyEncodedCrossDeployCalldata:
      ReadyEncodedCrossDeployCalldataSchema.mapDomainToReportText(
        deploymentPackage.getReadyEncodedCrossDeployCalldata()
      ),
    linkedContractsFactoryAddress:
      LinkedContractsFactoryAddressSchema.mapDomainToReportText(
        deploymentPackage.getLinkedContractsFactoryAddress()
      ),
  }
}

export function mapReportTextToDomain(
  reportText: CrossChainDeploymentPackageReportText
): CrossChainDeploymentPackage {
  const {
    profileAddress,
    initialControllerAddress,
    initialChainID,
    primaryContractDeployment,
    secondaryContractDeployment,
    postDeploymentModule,
    postDeploymentModuleCalldata,
    readyEncodedCrossDeployCalldata,
    linkedContractsFactoryAddress,
  } = reportText
  return CrossChainDeploymentPackage.create({
    profileAddress: ProfileAddressSchema.mapReportTextToDomain(profileAddress),
    initialControllerAddress:
      InitialControllerAddressSchema.mapReportTextToDomain(
        initialControllerAddress
      ),
    initialChainID: InitialChainIDSchema.mapReportTextToDomain(initialChainID),
    primaryContractDeployment:
      PrimaryContractDeploymentSchema.mapReportTextToDomain(
        primaryContractDeployment
      ),
    secondaryContractDeployment:
      SecondaryContractDeploymentSchema.mapReportTextToDomain(
        secondaryContractDeployment
      ),
    postDeploymentModule:
      PostDeploymentModuleSchema.mapReportTextToDomain(postDeploymentModule),
    postDeploymentModuleCalldata:
      PostDeploymentModuleCalldataSchema.mapReportTextToDomain(
        postDeploymentModuleCalldata
      ),
    readyEncodedCrossDeployCalldata:
      ReadyEncodedCrossDeployCalldataSchema.mapReportTextToDomain(
        readyEncodedCrossDeployCalldata
      ),
    linkedContractsFactoryAddress:
      LinkedContractsFactoryAddressSchema.mapReportTextToDomain(
        linkedContractsFactoryAddress
      ),
  })
}
